import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { calculateResumeProgress } from '../../utils/progressSync';
// CSS는 latte-components.css에 통합됨

/**
 * 이력서 진행률을 계산하고 표시하는 공통 컴포넌트
 * @param {Object} resumeData - 이력서 데이터
 * @param {boolean} showBar - 진행률 바 표시 여부 (기본: true)
 * @param {string} className - 추가 CSS 클래스
 * @param {string} size - 크기 ('sm', 'md', 'lg') (기본: 'md')
 */
const ProgressIndicator = ({ 
  resumeData, 
  showBar = true, 
  className = "", 
  size = "md",
  showText = true 
}) => {

  /**
   * 진행률에 따른 색상 클래스 반환
   */
  const getProgressColorClass = (progress) => {
    if (progress === 100) return 'progress-gradient-complete';
    if (progress >= 80) return 'progress-gradient-high';
    if (progress >= 60) return 'progress-gradient-medium';
    if (progress >= 40) return 'progress-gradient-low';
    if (progress >= 20) return 'progress-gradient-start';
    return 'progress-gradient-initial';
  };

  /**
   * 진행률에 따른 텍스트 색상 클래스 반환
   */
  const getProgressTextClass = (progress) => {
    if (progress === 100) return 'progress-text-complete';
    if (progress >= 80) return 'progress-text-high';
    if (progress >= 60) return 'progress-text-medium';
    if (progress >= 40) return 'progress-text-low';
    if (progress >= 20) return 'progress-text-start';
    return 'progress-text-initial';
  };

  /**
   * 진행률에 따른 상태 텍스트 반환
   */
  const getProgressStatus = (progress) => {
    if (progress === 100) return '완성';
    if (progress > 0) return `${progress}% 진행`;
    return '시작 전';
  };

  /**
   * 크기에 따른 클래스 반환
   */
  const getSizeClass = () => {
    const sizeMap = {
      'sm': 'progress-sm',
      'md': 'progress-md',
      'lg': 'progress-lg'
    };
    return sizeMap[size] || sizeMap['md'];
  };

  const getWidthClass = () => {
    const widthMap = {
      'sm': 'progress-w-sm',
      'md': 'progress-w-md',
      'lg': 'progress-w-lg'
    };
    return widthMap[size] || widthMap['md'];
  };

  const getTextSizeClass = () => {
    const textMap = {
      'sm': 'text-xs',
      'md': 'text-sm',
      'lg': 'text-base'
    };
    return textMap[size] || textMap['md'];
  };

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  
  const progress = calculateResumeProgress(resumeData);
  const progressColorClass = getProgressColorClass(progress);
  const progressTextClass = getProgressTextClass(progress);
  const progressStatus = getProgressStatus(progress);
  const sizeClass = getSizeClass();
  const widthClass = getWidthClass();
  const textSizeClass = getTextSizeClass();

  // 90% 진행률일 때 툴팁 메시지
  const getTooltipMessage = () => {
    if (progress === 90) {
      return '이력서 편집 화면에서 이메일과 연락처를 기입해주세요!';
    }
    return null;
  };

  const tooltipMessage = getTooltipMessage();

  // 툴팁 위치 계산
  const updateTooltipPosition = () => {
    if (containerRef.current && tooltipMessage) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 50,
        left: rect.left + rect.width / 2
      });
    }
  };

  const handleMouseEnter = () => {
    if (tooltipMessage) {
      updateTooltipPosition();
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // 윈도우 리사이즈 시 툴팁 위치 업데이트
  useEffect(() => {
    const handleResize = () => {
      if (showTooltip) {
        updateTooltipPosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showTooltip]);

  return (
    <div 
      ref={containerRef}
      className={`${className} relative progress-container`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showText && (
        <div className={`${progressTextClass} mb-1 ${textSizeClass} font-medium ${tooltipMessage ? 'cursor-pointer' : ''}`}>
          <span>진행률: {progress}%</span>
        </div>
      )}
      
      {showBar && (
        <div className="relative">
          <div className={`${widthClass} progress-bg ${sizeClass} ${tooltipMessage ? 'cursor-pointer' : ''}`}>
            <div
              className={`progress-bar ${progressColorClass} relative overflow-hidden`}
              style={{ width: `${progress}%`, height: '100%' }}
            >
              {/* 100% 완성시 반짝이는 효과 */}
              {progress === 100 && (
                <div className="progress-shimmer"></div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Portal로 툴팁 렌더링 */}
      {tooltipMessage && showTooltip && createPortal(
        <div 
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="progress-tooltip relative">
            {tooltipMessage}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

/**
 * 진행률 뱃지 컴포넌트 (카드나 목록에서 사용)
 */
export const ProgressBadge = ({ resumeData, className = "" }) => {
  const progress = calculateResumeProgress(resumeData);

  const getBadgeClass = (progress) => {
    if (progress === 100) return 'progress-badge-complete';
    if (progress >= 80) return 'progress-badge-high';
    if (progress >= 60) return 'progress-badge-medium';
    if (progress >= 40) return 'progress-badge-low';
    if (progress >= 20) return 'progress-badge-start';
    return 'progress-badge-initial';
  };

  return (
    <span className={`progress-badge ${getBadgeClass(progress)} ${className}`}>
      {progress === 100 ? '완성' : 
       progress > 0 ? `${progress}% 진행` : 
       '시작 전'}
    </span>
  );
};

/**
 * 진행률 계산 로직만 사용하는 훅 (메모이제이션으로 최적화)
 */
export const useProgress = (resumeData) => {
  return React.useMemo(() => {
    return calculateResumeProgress(resumeData);
  }, [resumeData]);
};

export default ProgressIndicator;